import { ReactNode, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useBulkUpdateState } from '@shared/components/filter/state/BulkUpdateProvider';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  getComponentIcon,
  getComponentLabel,
  isFilterableComponent,
  isGroupableComponent,
  isInputComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { createCtx } from '@shared/util/context';
import { useStableValue } from '@shared/util/hooks';
import { v4 as uuidv4 } from '@shared/util/uuid';
import {
  ControlsActions,
  ControlsState,
  FilterOption,
  SortOption,
  SortSelectionState,
} from '../types';
import { PreserveCollectionCriteria } from './PreserveCollectionCriteria';
import { useViewTemplateActions, useViewTemplateState } from './ViewTemplateProvider';
import { isCollectionCriteriaEqual } from './isCollectionCriteriaEqual';
import { mapCollectionCriteriaToState } from './mapCollectionCriteriaToState';
import { mapStateToCollectionCriteria } from './mapStateToCollectionCriteria';
import { reducer } from './reducer';

const [useControlsState, ControlsStateProvider] = createCtx<ControlsState>();
const [useControlsActions, ControlsActionsProvider] = createCtx<ControlsActions>();
export { useControlsState, useControlsActions };

interface ProviderProps {
  children: ReactNode;
  workflowTemplateId: string;
}

export function ControlsProvider({ children, workflowTemplateId }: ProviderProps) {
  const { setViewTemplate } = useViewTemplateActions();
  const { viewTemplate } = useViewTemplateState();
  const [savedCriteria, setSavedCriteria] = useStableValue(viewTemplate.collectionCriteria);
  const components = useInputComponents(workflowTemplateId);
  const { showBulkFilterUpdate } = useBulkUpdateState();
  const [showGroupMenu, setShowGroupMenu] = useState(true);

  const availableFilters: FilterOption[] = useMemo(() => {
    return components.filter(isFilterableComponent).map(toFilterOption);
  }, [components]);

  const availableSorts: SortOption[] = useMemo(() => {
    return components.filter(c => c.isSortable).map(toSortOption);
  }, [components]);

  const availableGroups: FilterOption[] = useMemo(() => {
    return components.filter(isGroupableComponent).map(toFilterOption);
  }, [components]);

  const initialState = mapCollectionCriteriaToState(
    savedCriteria ?? null,
    availableFilters,
    availableSorts,
    availableGroups,
  );
  const [state, dispatch] = useReducer(reducer, initialState);

  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>(state.selectedFilters);
  const [selectedSorts, setSelectedSorts] = useState<SortOption[]>(state.selectedSorts);
  const [selectedGroups, setSelectedGroups] = useState<FilterOption[][]>(state.selectedGroups);
  const [search, setSearch] = useState<string | null>(null);
  const [isModified, setIsModified] = useState(false);

  const { filters, groups } = state;
  const sorts = useMemo(() => {
    const sortMap = new Map<string, SortSelectionState>();
    selectedSorts.forEach(item => {
      const uuid = uuidv4();
      const direction: API.SortDirection = item.direction ?? 'ASC';
      sortMap.set(uuid, {
        sort: { uuid, ...item },
        direction,
      });
    });
    return sortMap;
  }, [selectedSorts]);

  const setFilterOption: ControlsActions['setFilterOption'] = useCallback(
    (filter, operation, selection, negate) => {
      dispatch({ type: 'filter', operation, payload: { filter, selection, negate } });
    },
    [dispatch],
  );

  const setGroupOption: ControlsActions['setGroupOption'] = useCallback(
    (filter, groupIndex, selection) => {
      dispatch({ type: 'group', groupIndex, payload: { filter, selection } });
    },
    [dispatch],
  );

  const resetGroups: ControlsActions['resetGroups'] = useCallback(() => {
    dispatch({ type: 'resetGroups' });
  }, [dispatch]);

  const resetState = useCallback(
    (nextState: ControlsState) => {
      setSelectedFilters(nextState.selectedFilters);
      setSelectedSorts(nextState.selectedSorts);
      setSelectedGroups(nextState.selectedGroups);
      dispatch({ type: 'reset', payload: nextState });
    },
    [dispatch],
  );

  const revertCollectionCriteria: ControlsActions['revertCollectionCriteria'] = useCallback(() => {
    // revert state to saved version in viewTemplate
    const nextState = mapCollectionCriteriaToState(
      savedCriteria ?? null,
      availableFilters,
      availableSorts,
      availableGroups,
    );
    resetState(nextState);
  }, [savedCriteria, availableFilters, availableGroups, availableSorts, resetState]);

  useEffect(() => {
    // update local savedCriteria when viewTemplate collection criteria changes
    setSavedCriteria(prev => {
      if (isCollectionCriteriaEqual(prev ?? null, viewTemplate.collectionCriteria ?? null)) {
        return prev;
      } else {
        return viewTemplate.collectionCriteria ?? null;
      }
    });
  }, [viewTemplate, setSavedCriteria]);

  useEffect(() => {
    // reset state when savedCriteria changes
    revertCollectionCriteria();
  }, [savedCriteria, revertCollectionCriteria]);

  const updateViewTemplateCollectionCriteria: ControlsActions['updateViewTemplateCollectionCriteria'] =
    useCallback(() => {
      const updatedCriteria = mapStateToCollectionCriteria({ filters, sorts, groups });
      setViewTemplate(prev => ({ ...prev, collectionCriteria: updatedCriteria }));
    }, [filters, sorts, groups, setViewTemplate]);

  const controlsStateValue: ControlsState = useMemo(
    () => ({
      filters,
      groups: showBulkFilterUpdate ? [] : groups,
      availableFilters,
      selectedFilters,
      sorts,
      availableSorts,
      selectedSorts,
      availableGroups,
      selectedGroups,
      search,
      isModified,
      showGroupMenu,
    }),
    [
      availableFilters,
      availableGroups,
      availableSorts,
      search,
      selectedFilters,
      selectedGroups,
      selectedSorts,
      filters,
      sorts,
      groups,
      isModified,
      showBulkFilterUpdate,
      showGroupMenu,
    ],
  );

  const controlsActionsValue: ControlsActions = useMemo(
    () => ({
      setFilterOption,
      setSelectedFilters,
      setSelectedSorts,
      setGroupOption,
      setSelectedGroups,
      resetGroups,
      setSearch,
      revertCollectionCriteria,
      updateViewTemplateCollectionCriteria,
      setShowGroupMenu,
    }),
    [
      resetGroups,
      setFilterOption,
      setGroupOption,
      revertCollectionCriteria,
      setSearch,
      updateViewTemplateCollectionCriteria,
      setShowGroupMenu,
    ],
  );

  useEffect(() => {
    // When switching views, reset showGroupMenu to true. Calendar views will
    // set this value as needed for themselves.
    if (viewTemplate.subtype !== 'CALENDAR') {
      setShowGroupMenu(true);
    }
  }, [viewTemplate.subtype, setShowGroupMenu]);

  return (
    <ControlsActionsProvider value={controlsActionsValue}>
      <ControlsStateProvider value={controlsStateValue}>
        <PreserveCollectionCriteria setIsModified={setIsModified} resetState={resetState} />
        {children}
      </ControlsStateProvider>
    </ControlsActionsProvider>
  );
}

function useInputComponents(workflowTemplateId: string): InputComponent[] {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  return useMemo(() => {
    const components = workflowTemplate?.components ?? [];
    return components.filter(isInputComponent);
  }, [workflowTemplate]);
}

function toFilterOption(component: InputComponent): FilterOption {
  return {
    id: component.id,
    type: component.type,
    label: getComponentLabel(component) ?? '',
    icon: getComponentIcon(component),
    component,
  };
}

function toSortOption(component: InputComponent): SortOption {
  return {
    id: component.id,
    type: component.type,
    label: getComponentLabel(component) ?? '',
    icon: getComponentIcon(component),
    component,
  };
}
